import { Button, FormRoot, FormTextarea, RHF, zodResolver } from "@devhop/ui";
import { AlertTriangle } from "lucide-react";
import { z } from "zod";
import type {
  JDPositionMappingDTO,
  PositionWithJDDTO,
} from "@/modules/jd-mapping/domain/contracts";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

type JDItem = LookupItem;

const MappingFormSchema = z.object({
  jdId: z.string().min(1, "ກະລຸນາເລືອກ JD"),
  notes: z.string().optional(),
});

export type MappingFormValues = z.infer<typeof MappingFormSchema>;

type JDMappingFormProps = {
  position: PositionWithJDDTO;
  currentMapping?: JDPositionMappingDTO | null;
  onSubmit: (values: { jdId: string; notes?: string }) => void;
  submitting?: boolean;
  onCancel: () => void;
};

export function JDMappingForm({
  position,
  currentMapping,
  onSubmit,
  submitting,
  onCancel,
}: JDMappingFormProps) {
  const methods = RHF.useForm<MappingFormValues>({
    resolver: zodResolver(MappingFormSchema),
    defaultValues: {
      jdId: currentMapping?.jdId || "",
      notes: "",
    },
  });

  const isUpdate = !!currentMapping;

  return (
    <FormRoot<MappingFormValues>
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          jdId: vals.jdId,
          notes: vals.notes || undefined,
        });
      }}
      className="space-y-4"
    >
      <div className="space-y-2">
        <p className="font-medium text-sm">ຕໍາແໜ່ງ: {position.title}</p>
        <p className="text-muted-foreground text-xs">
          {position.departmentName || "-"}
        </p>
      </div>

      {isUpdate && (
        <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3">
          <p className="flex items-center gap-1 font-medium text-sm text-yellow-700 dark:text-yellow-400">
            <AlertTriangle className="h-4 w-4" />
            ການແຈ້ງເຕືອນ
          </p>
          <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-300">
            ການປ່ຽນ JD ອາດຈະສົ່ງຜົນກະທົບຕໍ່ KPI ແລະ ການປະເມີນຜົນງານທີ່ກຳລັງໃຊ້ງານຢູ່
          </p>
        </div>
      )}

      <FormInfiniteCombobox<JDItem>
        name="jdId"
        label="ເລືອກ JD"
        requiredMark
        queryKey={["jds", "mapping"]}
        queryFn={(args) =>
          fetchLookupForInfinite(`${config.apiUrl}/jd/lookup`, args)
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/jd/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກ JD ທີ່ຕ້ອງການຜູກກັບຕໍາແໜ່ງ"
      />

      <FormTextarea
        name="notes"
        label="ຫມາຍເຫດ (ບໍ່ບັງຄັບ)"
        placeholder="ຕົວຢ່າງ: ປ່ຽນເພື່ອປັບປຸງ KPI, ອັບເດດຕາມການປ່ຽນແປງຂອງອົງກອນ"
      />

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          ຍົກເລີກ
        </Button>
        <Button type="submit" isLoading={submitting}>
          {isUpdate ? "ປ່ຽນ JD" : "ຜູກ JD"}
        </Button>
      </div>
    </FormRoot>
  );
}
